import { Link } from 'react-router-dom';
import {
  Globe,
  Tags,
  Newspaper,
  ArrowRight,
  Plus,
  ExternalLink,
  Loader2,
  Phone,
  Images,
  MessageSquareQuote,
  BadgeDollarSign,
  Users,
} from 'lucide-react';
import {
  useWebsiteServices,
  useWebsiteBlogs,
  useWebsiteGallery,
  useWebsiteTestimonials,
  useWebsitePlans,
} from '../../services/httpServices/queries';
import { ENV } from '../../config/env';

const websiteUrl = ENV.VITE_WEBSITE_URL;

function StatCard({
  title,
  count,
  icon: Icon,
  to,
  loading,
}: {
  title: string;
  count: number;
  icon: React.ElementType;
  to: string;
  loading: boolean;
}) {
  return (
    <Link
      to={to}
      className="bg-surface border border-border rounded-xl p-5 hover:border-primary/50 transition-colors group"
    >
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon size={22} className="text-primary" />
        </div>
        <ArrowRight
          size={18}
          className="text-text-muted group-hover:text-primary group-hover:translate-x-0.5 transition-all"
        />
      </div>
      <p className="mt-4 text-2xl font-bold text-text">
        {loading ? <Loader2 size={20} className="animate-spin text-text-muted" /> : count}
      </p>
      <p className="text-sm text-text-muted">{title}</p>
    </Link>
  );
}

export function Website() {
  const services = useWebsiteServices(1, 5);
  const blogs = useWebsiteBlogs(1, 5);
  const gallery = useWebsiteGallery({ page: 1, limit: 1 });
  const testimonials = useWebsiteTestimonials({ page: 1, limit: 1 });
  const plans = useWebsitePlans({ page: 1, limit: 1 });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Globe className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-text">Website Management</h1>
            <p className="text-sm text-text-muted">Manage the content shown on the public website.</p>
          </div>
        </div>
        <a
          href={websiteUrl}
          target="_blank"
          rel="noreferrer"
          className="px-4 py-2 rounded-lg border border-border text-sm text-text hover:border-primary hover:text-primary transition-colors flex items-center gap-2"
        >
          <ExternalLink size={16} />
          View Website
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Services"
          count={services.data?.meta?.total ?? 0}
          icon={Tags}
          to="/website/services"
          loading={services.isLoading}
        />
        <StatCard
          title="Blog Posts"
          count={blogs.data?.meta?.total ?? 0}
          icon={Newspaper}
          to="/website/blogs"
          loading={blogs.isLoading}
        />
        <StatCard
          title="Gallery Assets"
          count={gallery.data?.meta?.total ?? 0}
          icon={Images}
          to="/website/gallery"
          loading={gallery.isLoading}
        />
        <StatCard
          title="Testimonials"
          count={testimonials.data?.meta?.total ?? 0}
          icon={MessageSquareQuote}
          to="/website/testimonials"
          loading={testimonials.isLoading}
        />
        <StatCard
          title="Plans"
          count={plans.data?.meta?.total ?? 0}
          icon={BadgeDollarSign}
          to="/website/plans"
          loading={plans.isLoading}
        />
        <Link
          to="/website/contact"
          className="bg-surface border border-border rounded-xl p-5 hover:border-primary/50 transition-colors group"
        >
          <div className="flex items-center justify-between">
            <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
              <Phone size={22} className="text-primary" />
            </div>
            <ArrowRight
              size={18}
              className="text-text-muted group-hover:text-primary group-hover:translate-x-0.5 transition-all"
            />
          </div>
          <p className="mt-4 text-2xl font-bold text-text">Manage</p>
          <p className="text-sm text-text-muted">Contact Page</p>
        </Link>
        <Link
          to="/roles?type=website"
          className="bg-surface border border-border rounded-xl p-5 hover:border-primary/50 transition-colors group"
        >
          <div className="flex items-center justify-between">
            <div className="w-11 h-11 rounded-lg bg-info/10 flex items-center justify-center">
              <Users size={22} className="text-info" />
            </div>
            <ArrowRight
              size={18}
              className="text-text-muted group-hover:text-primary group-hover:translate-x-0.5 transition-all"
            />
          </div>
          <p className="mt-4 text-2xl font-bold text-text">Manage</p>
          <p className="text-sm text-text-muted">Blog Authors</p>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent services */}
        <div className="bg-surface border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-text flex items-center gap-2">
              <Tags size={18} className="text-primary" /> Services
            </h2>
            <Link
              to="/website/services/new"
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              <Plus size={14} /> Add
            </Link>
          </div>
          <ul className="divide-y divide-border">
            {(services.data?.data || []).map((s) => (
              <li key={s.id} className="flex items-center gap-3 py-2.5">
                {s.icon ? (
                  <img src={s.icon} alt="" className="w-8 h-8 rounded object-contain bg-background" />
                ) : (
                  <span className="w-8 h-8 rounded bg-background" />
                )}
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/website/services/${s.id}/edit`}
                    className="text-sm text-text hover:text-primary truncate block"
                  >
                    {s.title}
                  </Link>
                  <p className="text-xs text-text-muted truncate">{s.slug}</p>
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    s.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                  }`}
                >
                  {s.isActive ? 'Active' : 'Inactive'}
                </span>
              </li>
            ))}
            {!services.isLoading && (services.data?.data || []).length === 0 && (
              <li className="py-6 text-center text-sm text-text-muted">No services yet.</li>
            )}
          </ul>
        </div>

        {/* Recent blogs */}
        <div className="bg-surface border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-text flex items-center gap-2">
              <Newspaper size={18} className="text-primary" /> Blog Posts
            </h2>
            <Link
              to="/website/blogs/new"
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              <Plus size={14} /> Add
            </Link>
          </div>
          <ul className="divide-y divide-border">
            {(blogs.data?.data || []).map((b) => (
              <li key={b.id} className="flex items-center gap-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/website/blogs/${b.id}/edit`}
                    className="text-sm text-text hover:text-primary truncate block"
                  >
                    {b.title}
                  </Link>
                  <p className="text-xs text-text-muted truncate">
                    {b.category || 'Uncategorized'} · {b.author}
                  </p>
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    b.isPublished ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                  }`}
                >
                  {b.isPublished ? 'Published' : 'Draft'}
                </span>
              </li>
            ))}
            {!blogs.isLoading && (blogs.data?.data || []).length === 0 && (
              <li className="py-6 text-center text-sm text-text-muted">No blog posts yet.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
